import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map, retry, tap} from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {Order} from "../../model/Order";

// The data types that used to be declared here now live in
// src/model/AddressValidation, so that code with no Angular dependency can use
// them -- AddressValidationOrder (a model) and the standalone smoke tests,
// which cannot require @angular/core. Re-exported because every existing
// import in the app names THIS module, and moving a type is not a reason to
// touch a dozen unrelated files.
export {AddressValidationOutcome, AddressValidationResult, ValidationAddress} from '../../model/AddressValidation';
import {AddressValidationResult} from '../../model/AddressValidation';

@Injectable()
export class AddressValidationService {
  private objects: Order[] = [];
  private objectsObservable = of( this.objects );
  public loadingState = 'INITIAL';


  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService) {
  }

  /**
   * Validate an address.
   *
   * ## This never emits `undefined`, and that is the point
   *
   * It used to route failures through `handleError`, which logs and then emits
   * `of(undefined)`. Every caller guards with `if (result)`, so a failed call
   * became a silent no-op at three call sites and an unbounded retry loop at a
   * fourth -- and at none of them did the operator learn that the validator had
   * not been reached. Worse, `cart-address-section` reads
   * `recommendedAddresses.length` off the result, so once a failure had been
   * turned into a falsy value the branch that ran next was the one meaning "the
   * validator answered and found nothing" -- the opposite of the truth.
   *
   * A failure is now an `AddressValidationResult` whose `getOutcome()` is
   * `ERROR`, carrying the message. `retry(3)` still covers the transient case
   * first, so reaching that path means four attempts have already failed.
   */
  validate (address: any): Observable<AddressValidationResult> {
    const url = `${environment.apiBaseURL}validateaddress`;
    return this.http.post<AddressValidationResult>(url, address, this.startupService.getHttpOptions())
      .pipe( retry(3),
        map(o => new AddressValidationResult( o )),
        catchError((error: any) => {
          console.error(error, 'address validation');
          const message = error && error.message ? error.message : 'address validation failed';
          this.log(`address validation failed: ${message}`);
          return of( AddressValidationResult.forError(message) );
        })
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  private log(message: string) {
    this.messageService.add('OrderService: ' + message);
  }

}
